import React, { useRef, useState } from "react";
import { ArrowRight, ArrowLeft, CircleX } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export interface ReferenceResource {
  id: string;
  type: "file" | "link" | "text";
  name: string;
  value: string | File;
}

interface Props {
  onBack: () => void;
  onSkip: () => void;
  onContinue: (resources: ReferenceResource[] | null) => void;
}

const MAX_RESOURCES = 5;

export const RoomExamStep2Reference: React.FC<Props> = ({
  onBack,
  onSkip,
  onContinue,
}) => {
  const [resources, setResources] = useState<ReferenceResource[]>([]);
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [pasteLink, setPasteLink] = useState("");
  const [pasteText, setPasteText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addResource = (res: Omit<ReferenceResource, "id">) => {
    if (resources.length >= MAX_RESOURCES) return;
    setResources((prev) => [...prev, { ...res, id: crypto.randomUUID() }]);
  };

  const removeResource = (id: string) => {
    setResources((prev) => prev.filter((r) => r.id !== id));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    addResource({ type: "file", name: file.name, value: file });
    // Reset so the same file can be re-selected
    e.target.value = "";
  };

  const handlePasteSubmit = () => {
    const link = pasteLink.trim();
    const text = pasteText.trim();
    if (link) {
      addResource({ type: "link", name: link, value: link });
    } else if (text) {
      const preview = text.length > 40 ? text.slice(0, 40) + "…" : text;
      addResource({ type: "text", name: preview, value: text });
    }
    setPasteLink("");
    setPasteText("");
    setPasteDialogOpen(false);
  };

  return (
    <section className="mb-6 flex w-full items-center justify-center">
      <div className="flex w-full flex-col items-center gap-4 rounded-3xl border border-border bg-card px-8 py-6 shadow-sm lg:px-16">
        {/* Stepper */}
        <div className="mb-3 flex w-full items-center justify-center">
          <div className="flex w-1/3 max-w-sm gap-2">
            <div className="h-1.5 flex-1 rounded-full bg-muted">
              <div className="h-1.5 w-full rounded-full bg-emerald-500" />
            </div>
            <div className="h-1.5 flex-1 rounded-full bg-muted">
              <div className="h-1.5 w-full rounded-full bg-emerald-500" />
            </div>
            <div className="h-1.5 flex-1 rounded-full bg-muted" />
          </div>
        </div>

        {/* Title */}
        <div className="flex max-w-2xl flex-col items-center gap-1 text-center">
          <h2 className="text-2xl font-medium sm:text-3xl">
            Have a practice exam or cheatsheet for reference?
          </h2>
          <p className="text-base text-muted-foreground">
            We'll use this to make the exam as accurate as possible
          </p>
        </div>

        {/* Upload + Paste Options */}
        <div className="flex flex-col items-center justify-center">
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp"
              onChange={handleFileSelect}
            />

            {/* Upload */}
            <div
              onClick={() => {
                if (resources.length < MAX_RESOURCES) fileInputRef.current?.click();
              }}
              className="group relative cursor-pointer rounded-3xl border border-border bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md w-full sm:w-64"
            >
              <div className="flex h-[96px] flex-col items-start justify-center gap-y-1 px-5 py-3 sm:flex-col">
                <div className="flex items-center gap-x-3 space-y-2 sm:block">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mb-2 h-6 w-6 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" x2="12" y1="3" y2="15" />
                  </svg>
                  <div className="flex flex-col justify-center text-left">
                    <h3 className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground sm:text-base">
                      Upload
                    </h3>
                    <p className="text-xs text-left text-muted-foreground/70 transition-colors group-hover:text-muted-foreground sm:text-sm">
                      File, audio, video
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Paste */}
            <div
              onClick={() => {
                if (resources.length < MAX_RESOURCES) setPasteDialogOpen(true);
              }}
              className="group relative cursor-pointer rounded-3xl border border-border bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md w-full sm:w-64"
            >
              <div className="flex h-[96px] flex-col items-start justify-center gap-y-1 px-5 py-3 sm:flex-col">
                <div className="flex items-center gap-x-3 space-y-2 sm:block">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mb-2 h-6 w-6 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" aria-hidden="true">
                    <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                    <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
                    <line x1="8" x2="16" y1="12" y2="12" />
                  </svg>
                  <div className="flex flex-col justify-center text-left">
                    <h3 className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground sm:text-base">
                      Paste
                    </h3>
                    <p className="text-xs text-left text-muted-foreground/70 transition-colors group-hover:text-muted-foreground sm:text-sm">
                      YouTube, website, text
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resource Pills */}
        {resources.length > 0 && (
          <div className="w-full max-w-lg space-y-1 pt-1">
            <p className="text-xs text-muted-foreground">
              Contents ({resources.length}/{MAX_RESOURCES})
            </p>
            {resources.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between rounded-[1.25rem] bg-muted/50 p-2 pl-4"
              >
                <span className="flex-1 truncate text-sm">{r.name}</span>
                <button
                  type="button"
                  onClick={() => removeResource(r.id)}
                  className="ml-2 inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none h-9"
                >
                  <CircleX className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Buttons */}
        <div className="mb-2 mt-3 flex gap-3">
          <button
            onClick={onBack}
            className="flex h-10 w-12 items-center justify-center rounded-xl border border-border text-foreground transition hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          <button
            onClick={onSkip}
            className="h-10 rounded-xl px-4 text-sm font-medium transition hover:bg-muted"
          >
            Skip
          </button>

          <button
            onClick={() =>
              onContinue(resources.length > 0 ? resources : null)
            }
            className="inline-flex h-10 items-center justify-center gap-1 rounded-xl bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Continue
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Lightweight Paste Dialog */}
      <Dialog open={pasteDialogOpen} onOpenChange={setPasteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add a link or text</DialogTitle>
            <DialogDescription>
              Paste a URL or text to use as reference material
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">URL</label>
              <Input
                placeholder="https://..."
                value={pasteLink}
                onChange={(e) => setPasteLink(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                Or paste text
              </label>
              <Textarea
                placeholder="Paste notes, cheatsheet text, etc."
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                rows={4}
              />
            </div>
            <Button
              className="w-full"
              disabled={!pasteLink.trim() && !pasteText.trim()}
              onClick={handlePasteSubmit}
            >
              Add Reference
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
